import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("es-MX") : n ?? "—");

// Phase 9.8 — Widget: monthly coverage semaphore for /upload.
// Rows = report_types, columns = year-months. Green ✓ = data present,
// red ✗ = missing. Gaps section highlights months without data BETWEEN
// the min and max load date of every report type.
export default function CoverageMatrix() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCoverage = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/uploads/coverage");
      setData(r.data);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoverage();
  }, []);

  const labels = data?.labels || [];
  const rows = data?.report_types || [];

  // Aggregate every gap across every report_type so the alert box can list
  // "Possibly missing months" in one place.
  const allGaps = useMemo(() => {
    if (!data) return [];
    const flat = [];
    for (const rt of rows) {
      for (const g of rt.gaps || []) {
        flat.push({ report_type: rt.name, period: g });
      }
    }
    return flat;
  }, [data, rows]);

  // Build a per-cell lookup: has_data[{name}::{period}] = row_count
  const cellData = useMemo(() => {
    const map = {};
    for (const rt of rows) {
      for (const m of rt.months_with_data || []) {
        map[`${rt.name}::${m.period}`] = m.row_count;
      }
    }
    return map;
  }, [rows]);

  const cellStateFor = (rt, period) => {
    const key = `${rt.name}::${period}`;
    if (cellData[key] !== undefined) return { state: "has", count: cellData[key] };
    // Between min_month and max_month → real gap
    if (rt.min_month && rt.max_month && period >= rt.min_month && period <= rt.max_month) {
      return { state: "gap", count: 0 };
    }
    // Out of range for this report_type → not applicable
    return { state: "na", count: 0 };
  };

  return (
    <Card data-testid="coverage-matrix-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            {t("coverage.title", { defaultValue: "Semáforo de cobertura mensual" })}
          </CardTitle>
          <CardDescription>
            {t("coverage.subtitle", {
              defaultValue:
                "Muestra qué meses de cada tipo de reporte tienen datos cargados. Los huecos entre la primera y última carga aparecen como posibles cargas faltantes.",
            })}
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchCoverage}
          disabled={loading}
          data-testid="coverage-refresh-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        {loading && !data && (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t("common.loading")}
          </div>
        )}
        {data && rows.length > 0 && (
          <div
            className="overflow-x-auto"
            data-testid="coverage-matrix-scroll"
          >
            <table className="w-full text-xs" data-testid="coverage-matrix-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="sticky left-0 z-10 bg-card px-2 py-2 text-left font-medium">
                    {t("coverage.reportType", { defaultValue: "Tipo de reporte" })}
                  </th>
                  {labels.map((lab) => (
                    <th
                      key={lab}
                      className="px-1.5 py-2 text-center font-mono text-[10px] text-muted-foreground"
                    >
                      {lab.slice(2)}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-right font-medium">
                    {t("common.total")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((rt) => (
                  <tr
                    key={rt.name}
                    className="border-b border-border/40 hover:bg-muted/20"
                    data-testid={`coverage-row-${rt.name}`}
                  >
                    <td className="sticky left-0 z-10 bg-card px-2 py-1 font-mono text-[11px]">
                      {rt.name}
                      <span className="ml-1.5 text-[9px] text-muted-foreground">
                        ({rt.kind})
                      </span>
                    </td>
                    {labels.map((lab) => {
                      const { state, count } = cellStateFor(rt, lab);
                      const testid = `coverage-cell-${rt.name}-${lab}`;
                      if (state === "has") {
                        return (
                          <td key={lab} className="px-1 py-1 text-center" data-testid={testid}>
                            <div
                              className="mx-auto inline-flex h-6 w-6 items-center justify-center rounded bg-emerald-500/15 text-emerald-500"
                              title={`${lab}: ${fmt(count)} filas`}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </div>
                          </td>
                        );
                      }
                      if (state === "gap") {
                        return (
                          <td key={lab} className="px-1 py-1 text-center" data-testid={testid}>
                            <div
                              className="mx-auto inline-flex h-6 w-6 items-center justify-center rounded bg-rose-500/15 text-rose-500"
                              title={`${lab}: sin datos (posible carga faltante)`}
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </div>
                          </td>
                        );
                      }
                      // n/a: out of range
                      return (
                        <td key={lab} className="px-1 py-1 text-center" data-testid={testid}>
                          <div
                            className="mx-auto inline-flex h-6 w-6 items-center justify-center text-muted-foreground/30"
                            title={`${lab}: fuera de rango para ${rt.name}`}
                          >
                            ·
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-2 py-1 text-right font-mono tabular-nums">
                      {fmt(rt.total_rows)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t("coverage.empty", { defaultValue: "Aún no hay cargas registradas." })}
          </p>
        )}
        {allGaps.length > 0 && (
          <Alert
            className="border-amber-500/40 bg-amber-50 dark:bg-amber-950/30"
            data-testid="coverage-gaps-alert"
          >
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-700 dark:text-amber-300">
              {t("coverage.gapsTitle", {
                defaultValue: "Posibles meses sin carga detectados",
                count: allGaps.length,
              })}
            </AlertTitle>
            <AlertDescription className="text-amber-700/80 dark:text-amber-200/80">
              <div className="mt-1 flex flex-wrap gap-1.5">
                {allGaps.map((g, i) => (
                  <Badge
                    key={`${g.report_type}-${g.period}-${i}`}
                    variant="outline"
                    className="border-amber-500/50 bg-amber-100/40 text-[10px] font-mono dark:bg-amber-900/30"
                    data-testid={`coverage-gap-${g.report_type}-${g.period}`}
                  >
                    {g.report_type} · {g.period}
                  </Badge>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
